import {FATCA_CRS_CONFIRM_KEYS} from '@sollapay/enums';
import {Checkbox, Typography} from '@sollapay/ui/components';
import {cn} from '@sollapay/ui/lib';
import {useWatch} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';

import {DeclarationConsentRow} from './DeclarationConsentRow';
import {useDeclarationSigner} from '../hooks';

import type {SupplementaryDeclarationValues} from './fatca-crs-supplementary.types';
import type {DeclarationSigner} from '../utils';
import type {FC} from 'react';
import type {Control, Path, UseFormSetValue} from 'react-hook-form';

export interface SupplementaryConfirmationsProps {
  /** Trustee / lawyer name shown in the signature confirmation text. */
  lawyerName?: string;
  control: Control<SupplementaryDeclarationValues>;
  /** Required to update both BENEFICIARIES_UNCHANGED and UPDATE_COMMITMENT from the combined checkbox. */
  setValue: UseFormSetValue<SupplementaryDeclarationValues>;
  disabled?: boolean;
}

const PREFIX = 'activation.packages.compliance.supplementary';

const SIGNATURE_BODY_KEYS: Record<DeclarationSigner, string> = {
  trustee: `${PREFIX}.confirmations.signature.body`,
  onBehalf: `${PREFIX}.confirmations.signature.bodyOnBehalf`,
  guest: `${PREFIX}.confirmations.signature.bodyGuest`
};

const BENEFICIARIES_UNCHANGED_PATH =
  `declarationValues.${FATCA_CRS_CONFIRM_KEYS.BENEFICIARIES_UNCHANGED}` as Path<SupplementaryDeclarationValues>;
const UPDATE_COMMITMENT_PATH =
  `declarationValues.${FATCA_CRS_CONFIRM_KEYS.UPDATE_COMMITMENT}` as Path<SupplementaryDeclarationValues>;
const SIGNATURE_PATH =
  `declarationValues.${FATCA_CRS_CONFIRM_KEYS.SIGNATURE}` as Path<SupplementaryDeclarationValues>;

export const SupplementaryConfirmations: FC<SupplementaryConfirmationsProps> = ({
  lawyerName,
  control,
  setValue,
  disabled = false
}) => {
  const {t} = useTranslation('trustAccounts');
  const signer = useDeclarationSigner();

  // One UI checkbox drives two backend keys, so it can't bind to a single field via Controller.
  const combinedChecked = useWatch({control, name: BENEFICIARIES_UNCHANGED_PATH}) === true;

  const handleCombinedChange = (checked: boolean) => {
    setValue(BENEFICIARIES_UNCHANGED_PATH, checked, {shouldDirty: true});
    setValue(UPDATE_COMMITMENT_PATH, checked, {shouldDirty: true});
  };

  return (
    <div className="flex flex-col gap-6" data-slot="supplementary-confirmations">
      {/* Account-holder approval (one combined checkbox) */}
      <div className="flex flex-col gap-3">
        <Typography weight="semibold" className="whitespace-normal">
          {t(`${PREFIX}.confirmations.sectionHeading`)}
        </Typography>
        <div className="overflow-hidden rounded-xl border border-border">
          <label
            htmlFor="confirmation-combined"
            className={cn(
              'flex items-start gap-3 px-4 py-3 transition-colors',
              disabled ? 'cursor-default' : 'cursor-pointer'
            )}
          >
            <Checkbox
              id="confirmation-combined"
              checked={combinedChecked}
              onCheckedChange={checked => handleCombinedChange(checked === true)}
              disabled={disabled}
              className="mt-0.5 shrink-0"
            />
            <Typography size="sm" className="leading-relaxed whitespace-normal">
              <Trans
                i18nKey={`${PREFIX}.confirmations.beneficiariesUnchangedNature.body`}
                ns="trustAccounts"
                components={{b: <strong className="font-semibold" />}}
              />
            </Typography>
          </label>
        </div>
      </div>

      {/* Signature (one checkbox) */}
      <div className="flex flex-col gap-3">
        <Typography weight="semibold" className="whitespace-normal">
          {t(`${PREFIX}.confirmations.signatureSectionHeading`)}
        </Typography>
        <div className="overflow-hidden rounded-xl border border-border">
          <DeclarationConsentRow
            name={SIGNATURE_PATH}
            control={control}
            id="confirmation-signature"
            disabled={disabled}
          >
            {t(SIGNATURE_BODY_KEYS[signer], {lawyerName: lawyerName ?? ''})}
          </DeclarationConsentRow>
        </div>
      </div>
    </div>
  );
};
